import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateRelocation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const password = sessionStorage.getItem('admin_password');

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    pet_name: '',
    pet_type: 'Dog',
    pet_breed: '',
    pet_age: '',
    pet_weight: '',
    microchip_number: '',
    origin: '',
    destination: '',
    travel_date: '',
    arrival_date: '',
    airline: '',
    flight_number: '',
    flight_details: '',
    current_status: 'quote_sent',
    notes: '',
    special_requirements: ''
  });

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchClients();
    if (isEditMode) {
      fetchRelocation();
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      const data = await response.json();
      setClients(data);
    } catch (error) {
      logger.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    }
  };

  const fetchRelocation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/client/relocations/${id}`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      const data = await response.json();

      // Convert dates to YYYY-MM-DD for <input type="date">.
      // Use Cayman timezone so "May 10 in Cayman" always shows as 05/10 in the input,
      // regardless of how the date is stored (plain YYYY-MM-DD or full ISO timestamp).
      const toDateInput = (val) => {
        if (!val) return '';
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Cayman',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(val));
      };
      data.travel_date = toDateInput(data.travel_date);
      data.arrival_date = toDateInput(data.arrival_date);

      setFormData(data);
    } catch (error) {
      logger.error('Error fetching relocation:', error);
      toast.error('Failed to load relocation details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEditMode
        ? `${API_URL}/api/admin/relocations/${id}`
        : `${API_URL}/api/admin/relocations`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEditMode ? 'Relocation updated successfully!' : 'Relocation created successfully!');
        navigate('/admin/relocations');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save relocation');
      }
    } catch (error) {
      logger.error('Error saving relocation:', error);
      toast.error('An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const statusOptions = [
    { value: 'quote_sent', label: 'Quote Sent' },
    { value: 'booking_confirmed', label: 'Booking Confirmed' },
    { value: 'documents_required', label: 'Documents Required' },
    { value: 'documents_submitted', label: 'Documents Submitted' },
    { value: 'documents_under_review', label: 'Documents Under Review' },
    { value: 'documents_approved', label: 'Documents Approved' },
    { value: 'travel_arrangements', label: 'Travel Arrangements' },
    { value: 'flight_booked', label: 'Flight Booked' },
    { value: 'pre_travel_prep', label: 'Pre-Travel Prep' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'complete', label: 'Complete' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/relocations')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditMode ? 'Edit Relocation' : 'New Relocation'}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {isEditMode ? 'Update pet relocation details' : 'Create a new pet relocation record'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-8">
          {/* Client Selection */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client *
                </label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  disabled={isEditMode}
                >
                  <option value="">Select a client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} ({client.email})
                    </option>
                  ))}
                </select>
                {isEditMode && (
                  <p className="mt-1 text-xs text-gray-500">Client cannot be changed after creation</p>
                )}
              </div>
            </div>
          </div>

          {/* Pet Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pet Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet Name *
                </label>
                <input
                  type="text"
                  name="pet_name"
                  value={formData.pet_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., Max"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pet Type *
                </label>
                <select
                  name="pet_type"
                  value={formData.pet_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="Dog">Dog</option>
                  <option value="Cat">Cat</option>
                  <option value="Bird">Bird</option>
                  <option value="Rabbit">Rabbit</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Breed
                </label>
                <input
                  type="text"
                  name="pet_breed"
                  value={formData.pet_breed}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., Golden Retriever"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age
                </label>
                <input
                  type="text"
                  name="pet_age"
                  value={formData.pet_age}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., 3 years"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight
                </label>
                <input
                  type="text"
                  name="pet_weight"
                  value={formData.pet_weight}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., 25kg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Microchip Number
                </label>
                <input
                  type="text"
                  name="microchip_number"
                  value={formData.microchip_number}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., 123456789012345"
                />
              </div>
            </div>
          </div>

          {/* Travel Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Travel Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Origin *
                </label>
                <input
                  type="text"
                  name="origin"
                  value={formData.origin}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., Cayman Islands"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Destination *
                </label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., United Kingdom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Travel Date
                </label>
                <input
                  type="date"
                  name="travel_date"
                  value={formData.travel_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arrival Date
                </label>
                <input
                  type="date"
                  name="arrival_date"
                  value={formData.arrival_date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Flight Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Flight Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Airline
                </label>
                <input
                  type="text"
                  name="airline"
                  value={formData.airline}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., British Airways"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flight Number
                </label>
                <input
                  type="text"
                  name="flight_number"
                  value={formData.flight_number}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., BA2156"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flight Details
                </label>
                <textarea
                  name="flight_details"
                  value={formData.flight_details}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Additional flight information..."
                />
              </div>
            </div>
          </div>

          {/* Status & Notes */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status & Notes</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Status *
                </label>
                <select
                  name="current_status"
                  value={formData.current_status}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requirements
                </label>
                <textarea
                  name="special_requirements"
                  value={formData.special_requirements}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., Requires temperature-controlled environment, medication schedule..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Internal Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Admin notes and comments..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/relocations')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Relocation' : 'Create Relocation'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
